import React, { useState } from 'react';
import api from '@/lib/api';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface NewsSearchProps {
  onResultsFound: (results: any[]) => void;
}

const TIME_PERIODS = [
  { label: 'Letzte 24 Stunden', value: '1d' },
  { label: 'Letzte Woche', value: '1w' },
  { label: 'Letzter Monat', value: '1m' },
  { label: 'Letzte 3 Monate', value: '3m' },
  { label: 'Letztes Jahr', value: '1y' },
];

export const NewsSearch: React.FC<NewsSearchProps> = ({ onResultsFound }) => {
  const [query, setQuery] = useState('');
  const [location, setLocation] = useState('');
  const [timePeriod, setTimePeriod] = useState('1m');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSearch = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      console.log('Sending search request with data:', { query, location, time_period: timePeriod });
      
      // Log the full request URL and configuration
      console.log('Request URL:', '/api/search');
      
      const response = await api.post('/api/search', {
        query: query,
        location: location,
        time_period: timePeriod
      });

      console.log('Full response:', response);

      // Check if response.data is the array directly
      const results = Array.isArray(response.data) ? response.data :
                     response.data.results ? response.data.results :
                     response.data.data ? response.data.data : [];

      console.log('Processed results:', results);

      if (results && results.length > 0) {
        onResultsFound(results);
      } else {
        setError('Keine Ergebnisse gefunden');
      }
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        console.error('Response status:', err.response?.status);
        console.error('Response data:', err.response?.data);
        console.error('Request URL:', err.config?.url);
        console.error('Request method:', err.config?.method);
        console.error('Request data:', err.config?.data);
        console.error('Request headers:', err.config?.headers);
        
        // Log the full error for debugging
        console.error('Full axios error:', JSON.stringify({
          message: err.message,
          code: err.code,
          status: err.response?.status,
          statusText: err.response?.statusText,
          headers: err.response?.headers,
          data: err.response?.data
        }, null, 2));
        
        if (!err.response) {
          setError('Netzwerkfehler: Bitte überprüfen Sie Ihre Internetverbindung oder versuchen Sie es später erneut.');
        } else if (err.response.status === 405) {
          setError('API-Konfigurationsfehler: Bitte versuchen Sie es später erneut.');
        } else if (err.response.status === 503) {
          setError('Der Service ist momentan nicht verfügbar. Bitte versuchen Sie es später erneut.');
        } else if (err.response.status === 404) {
          setError('API-Endpunkt nicht gefunden. Bitte stellen Sie sicher, dass der Backend-Server auf Port 8000 läuft.');
        } else {
          setError(`Ein Fehler ist aufgetreten: ${err.response?.data?.message || err.message}`);
        }
      } else {
        setError('Ein unerwarteter Fehler ist aufgetreten');
      }
      console.error('Full error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="grid gap-6 p-6 border rounded-lg">
      <h2 className="text-xl font-semibold">Projektsuche</h2>
      <form onSubmit={handleSearch} className="space-y-4">
        <div className="grid grid-cols-1 gap-4">
          <div>
            <Label htmlFor="query">Projektkategorie</Label>
            <Input
              id="query"
              placeholder="z.B. Krankenhaus, Straßenbau, Schule..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div>
            <Label htmlFor="location">Standort</Label>
            <Input
              id="location"
              placeholder="z.B. Berlin, München, Hamburg..."
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div>
            <Label htmlFor="timePeriod">Zeitraum</Label>
            <Select value={timePeriod} onValueChange={setTimePeriod}>
              <SelectTrigger id="timePeriod">
                <SelectValue placeholder="Wählen Sie einen Zeitraum" />
              </SelectTrigger>
              <SelectContent>
                {TIME_PERIODS.map((period) => (
                  <SelectItem key={period.value} value={period.value}>
                    {period.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button
          type="submit"
          disabled={isLoading}
          className="w-full"
          variant="default"
        >
          {isLoading ? 'Suche läuft...' : '🔍 Projekte finden'}
        </Button>
      </form>

      {error && (
        <div className="mt-4 p-2 text-destructive bg-destructive/10 rounded">
          {error}
        </div>
      )}
    </div>
  );
}; 